
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2, Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const UpdatePassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  // Check if hash fragment in URL from password reset email
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      console.log("[UpdatePassword] URL contains hash fragment - likely from password reset email");
    } else {
      console.log("[UpdatePassword] No hash fragment in URL");
    }
  }, []);

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage(null);
    
    // Simple validation
    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match");
      setLoading(false);
      return;
    }
    
    try {
      console.log("[UpdatePassword] Updating user password");
      
      const { error } = await supabase.auth.updateUser({
        password: password
      });
      
      if (error) {
        console.error("[UpdatePassword] Update error:", error);
        setErrorMessage(error.message);
        toast.error("Password update failed: " + error.message);
      } else {
        console.log("[UpdatePassword] Password updated successfully");
        toast.success("Password updated successfully!");
        
        // Short delay before redirecting
        setTimeout(() => {
          navigate("/sign-in");
        }, 1500);
      }
    } catch (error: any) {
      console.error("[UpdatePassword] Update exception:", error);
      setErrorMessage(error.message || "Something went wrong. Please try again.");
      toast.error("Failed to update password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-12 flex flex-col items-center">
      <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-xl shadow-lg border border-heaven-gold/20">
        <div className="text-center">
          <h1 className="text-3xl font-playfair font-bold text-gray-800">Update Password</h1>
          <p className="mt-2 text-gray-600">Enter your new password</p>
        </div>

        {errorMessage && (
          <Alert variant="destructive" className="border-red-500 text-red-700 bg-red-50">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleUpdatePassword} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="password">New Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-500">Password must be at least 6 characters long</p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
          </div>
          
          <Button
            type="submit"
            variant="gold"
            className="w-full py-6"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating Password...
              </>
            ) : (
              "Update Password"
            )}
          </Button>

          <div className="text-center">
            <Button
              variant="link"
              className="text-heaven-gold hover:text-heaven-dark-gold"
              onClick={() => navigate("/sign-in")}
            >
              Back to Sign In
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdatePassword;
